/**
 * 云端数据层（曲谱 / 版本 / 分享）。
 *
 * ## 为什么把散在 TestApp 里的调用收拢到这里
 *
 * 迁移前 TestApp 直接调 `supabase.from('scores')...`，十几个调用点里
 * 混着"查重""留档旧版本""更新""记配额"这套顺序逻辑。任何一处漏了
 * 错误处理都不会报错，只会静默失败（这就是 supabase 那套
 * `{data, error}` 永不抛的设计最容易出问题的地方）。
 *
 * 现在把这些序列收成本文件里的几个函数：
 *   - saveNewScore / saveCopyOfScore / updateScoreWithArchive
 *   - 版本留档与裁剪在后端事务里完成，前端不再手工拼接
 * 顺序逻辑收敛之后，"更新曲谱会先留档"这件事只有一个地方说了算。
 *
 * ## user_id 不再由前端传
 *
 * 原来 `.insert({..., user_id: session.user.id})` 把身份交给客户端决定。
 * 现在一律由后端从会话取。RLS 仍是第二道防线，但没必要让越权尝试发出去。
 */
import { api } from './api'

export type CloudScore = {
	id: string
	title: string
	creator: string
	is_public: boolean
	updated_at: string | null
	created_at: string | null
	/** 来源分类："mine" = 我的曲谱，"course" = 跟我练100 保存进来的课程 */
	category: string
}

export type CloudScoreDetail = CloudScore & {
	content: string
	/** 这份曲谱是否属于当前登录者 —— "保存"按钮分流与跟弹限制都用它 */
	is_owner: boolean
}

export type CloudVersion = {
	id: string
	title: string
	created_at: string | null
	/** 用户自己写的备注（"改了第三段和弦"）。没写过就是空串，不是 null。 */
	note: string
	/**
	 * 编辑器里当前展示的是不是这一版（界面据此把「回滚」换成「当前」）。
	 *
	 * 由后端算：回滚过 → 那一版；没回滚过 → 最新的一条。
	 * 前端不自己推，是因为"没回滚"时该标哪一行属于数据规则，
	 * 放两处写迟早会不一致。
	 */
	is_active: boolean
}

export type CloudShareLink = {
	id: string
	created_at: string | null
	expires_at: string | null
}

/** 我的曲谱列表 */
export async function listScores(): Promise<CloudScore[]> {
	return (await api.get<CloudScore[]>('/scores')) ?? []
}

/** 读单份曲谱。404 表示不存在**或**不属于你（RLS 有意不区分）。 */
export async function loadScore(id: string): Promise<CloudScoreDetail> {
	return api.get<CloudScoreDetail>(`/scores/${id}`)
}

/**
 * 新建一份曲谱。
 *
 * 后端会再查一次配额并拒绝超额写入，所以这里的 checkQuota 只是为了
 * 提前给出友好提示（真正的闸门在服务端）。
 *
 * `category` 标记来源（"mine" / "course"），编曲练习库据此分页签展示。
 * 省略时后端按 "mine" 处理。
 */
export async function saveNewScore(title: string, content: string, category?: 'mine' | 'course'): Promise<string> {
	const data = await api.post<{id: string}>('/scores/create', {title, content, category})
	return data.id
}

/**
 * 更新曲谱（会先自动留档旧版本，再裁剪到最近 20 条）。
 *
 * ⚠️ 留档与更新在后端同一个事务里完成。原来在前端做要两次往返，
 * 且"读旧内容"与"写新内容"之间有窗口期，并发保存会丢版本。
 */
export async function updateScore(id: string, title: string, content: string): Promise<void> {
	await api.post(`/scores/${id}/update`, {title, content})
}

/** 只改标题（列表页改名用，不产生历史版本） */
export async function renameScore(id: string, title: string): Promise<void> {
	const current = await loadScore(id)
	await updateScore(id, title, current.content)
}

/** 查库里有没有同名曲谱，用于「覆盖 / 打开已有 / 另存」的决策 */
export async function findByTitle(title: string): Promise<CloudScore | null> {
	const all = await listScores()
	// 与原栈一致：按创建时间倒序取最近的那份同名谱
	const same = all
		.filter(s => s.title === title)
		.sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
	return same[0] ?? null
}

export async function deleteScore(id: string): Promise<void> {
	await api.post(`/scores/${id}/delete`)
}

/** 切换公开态（广场） */
export async function setPublic(id: string, isPublic: boolean): Promise<void> {
	await api.post(`/scores/${id}/public`, {is_public: isPublic})
}

// ---------------------------------------------------------------- 版本

export async function listVersions(scoreId: string): Promise<CloudVersion[]> {
	return (await api.get<CloudVersion[]>(`/scores/${scoreId}/versions`)) ?? []
}

export async function loadVersion(versionId: string): Promise<{title: string, content: string}> {
	return api.get(`/versions/${versionId}`)
}

/**
 * 记住"回滚到了哪一版"（null = 清空，退回"最新那条"）。
 *
 * 落库而不是只放前端 state：换设备、隔天再打开，"我停在哪一版"
 * 是这份曲谱的状态，不是这一屏的临时状态。
 */
export async function setActiveVersion(scoreId: string, versionId: string | null): Promise<void> {
	await api.post(`/scores/${scoreId}/active-version`, {version_id: versionId})
}

/**
 * 给某个历史版本写备注（空串即清空）。
 *
 * 备注是**事后**补的，所以它是一个独立接口而不是保存时的字段 ——
 * 留档发生在点「保存」的那一刻，那时用户不会停下来写说明。
 *
 * 上限 40 字（后端 NOTE_MAX_CHARS）：更长的在历史版本列表里显示不全，
 * 截断了又看不出改了什么。
 */
export async function setVersionNote(versionId: string, note: string): Promise<void> {
	await api.post(`/versions/${versionId}/note`, {note})
}

// ---------------------------------------------------------------- 分享

export async function listShareLinks(scoreId: string): Promise<CloudShareLink[]> {
	return (await api.get<CloudShareLink[]>(`/scores/${scoreId}/shares`)) ?? []
}

export async function createShareLink(scoreId: string): Promise<{id: string, expires_at: string}> {
	return api.post(`/scores/${scoreId}/shares/create`)
}

export async function deleteShareLink(id: string): Promise<void> {
	await api.post(`/shares/${id}/delete`)
}

/** 按分享 id 读曲谱 —— 访客可读，所以不要求登录 */
export async function loadSharedScore(shareId: string): Promise<{
	title: string
	content: string
	shared_at: string | null
}> {
	return api.get(`/shares/${shareId}`)
}